import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Gerencia o estado de pulses visuais (animação curta) por chave.
 * - Cancela o timer anterior ao retriggar a mesma chave (evita que o
 *   segundo clique encurte o pulse do primeiro).
 * - Cancela todos os timers pendentes em `destroy()` (evita callback
 *   rodando após `ngOnDestroy`).
 *
 * Uso:
 *   pulse = new PulseController<'foo' | 'bar'>(1050, () => this.cdr.markForCheck());
 *   this.pulse.trigger('foo');
 *   ngOnDestroy() { this.pulse.destroy(); }
 *
 *   <!-- template -->
 *   [class.is-pulsing]="pulse.isActive('foo')"
 */
export class PulseController<K extends string = string> {

  private readonly active: Set<K> = new Set<K>();
  private readonly subscriptions: Map<K, Subscription> = new Map<K, Subscription>();
  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly durationMs: number = 1050,
    private readonly onChange?: () => void,
  ) {}

  trigger(key: K): void {
    this.subscriptions.get(key)?.unsubscribe();

    this.active.add(key);
    this.onChange?.();

    const subscription: Subscription = timer(this.durationMs)
      .pipe(takeUntil(this.destroy$))
      .subscribe((): void => {
        this.active.delete(key);
        this.subscriptions.delete(key);
        this.onChange?.();
      });

    this.subscriptions.set(key, subscription);
  }

  isActive(key: K): boolean {
    return this.active.has(key);
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.clear();
    this.active.clear();
  }
}
